import React, { useEffect, useState } from "react";
import { auth } from "@/config/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Play, Pause, X, Image as ImageIcon, Music, Video, Search } from "lucide-react";
import Gallery from "@/pages/Gallery";

const API = process.env.REACT_APP_BACKEND_URL || "";
const SEASON = "2026-27";

// App-Soundtrack (liegt in /public) — erscheint auch in der Media-Bibliothek
const PUB = process.env.PUBLIC_URL || "";
const SOUNDTRACK = [
  { id: "song_als_sprache", type: "song", url: `${PUB}/rheinstrass.mp3`, title: "Rheinstrass als Sprache", ownerName: "App-Soundtrack" },
  { id: "song_kein_zentrum_v1", type: "song", url: `${PUB}/rheinstrass-kein-zentrum-v1.mp3`, title: "Rheinstrass – Kein Zentrum (V1)", ownerName: "App-Soundtrack" },
  { id: "song_kein_zentrum_v2", type: "song", url: `${PUB}/rheinstrass-kein-zentrum-v2.mp3`, title: "Rheinstrass – Kein Zentrum (V2)", ownerName: "App-Soundtrack" },
  { id: "song_kein_zentrum_v3", type: "song", url: `${PUB}/rheinstrass-kein-zentrum-v3.mp3`, title: "Rheinstrass – Kein Zentrum (V3)", ownerName: "App-Soundtrack" },
  { id: "song_fluss", type: "song", url: `${PUB}/rheinstrass-fluss.mp3`, title: "Wenn Rheinstraße ein Fluss wär", ownerName: "App-Soundtrack" },
];

const FILTERS = [
  { key: "all", label: "Alle" },
  { key: "image", label: "Bilder" },
  { key: "song", label: "Songs" },
  { key: "video", label: "Videos" },
  { key: "gallery", label: "Galerie" },
  { key: "mine", label: "Meine" },
];

export default function Media() {
  const [items, setItems] = useState(SOUNDTRACK);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const uid = auth.currentUser?.uid;

  useEffect(() => {
    (async () => {
      try {
        const token = await auth.currentUser.getIdToken();
        const res = await fetch(`${API}/api/market-ops/media/list?season_id=${SEASON}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        setItems([...SOUNDTRACK, ...(data.items || [])]);
      } catch (e) { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  const filtered = items.filter((it) => {
    if (filter === "mine") { if (it.ownerUserId !== uid) return false; }
    else if (filter !== "all" && it.type !== filter) return false;
    if (q && !(`${it.title} ${it.ownerName}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  const download = (url, name) => {
    const a = document.createElement("a");
    a.href = url; a.download = name || "media"; a.target = "_blank"; a.rel = "noreferrer";
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="min-h-screen bg-[#0a0608] text-white p-4 md:p-8" data-testid="media-page">
      <div className="max-w-6xl mx-auto space-y-5">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-amber-400">Media-Bibliothek</h1>
          <p className="text-white/50 text-sm mt-1">Alle Bilder, Songs & Videos des Abends — ansehen, anhören, herunterladen.</p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`media-filter-${f.key}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium ${filter === f.key ? "bg-amber-500 text-black" : "bg-white/10 text-white/70 hover:bg-white/20"}`}>{f.label}</button>
          ))}
          <div className="relative ml-auto">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche / Person…" className="bg-white/5 border-white/10 pl-9 w-56" data-testid="media-search" />
          </div>
        </div>

        {filter === "gallery" ? (
          <Gallery embedded />
        ) : loading ? <p className="text-white/40">Lädt…</p> : filtered.length === 0 ? (
          <p className="text-white/40 py-12 text-center">Keine Medien gefunden. (Admin: Index unter „Markt & Coins" aufbauen.)</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="media-grid">
            {filtered.map((it) => (
              <div key={it.id} className="bg-white/5 rounded-xl overflow-hidden border border-white/10">
                {it.type === "image" && (
                  <button onClick={() => setLightbox(it)} className="block w-full aspect-square">
                    <img src={it.url} alt={it.title} className="w-full h-full object-cover" loading="lazy" />
                  </button>
                )}
                {it.type === "song" && (
                  <div className="p-3 flex flex-col items-center justify-center aspect-square bg-gradient-to-br from-purple-900/40 to-indigo-900/20">
                    <Music size={36} className="text-purple-300 mb-2" />
                    <button onClick={() => setPlayingId(playingId === it.id ? null : it.id)} className="text-amber-300" data-testid={`media-play-${it.id}`}>
                      {playingId === it.id ? <Pause size={26} /> : <Play size={26} />}
                    </button>
                    {playingId === it.id && <audio src={it.url} autoPlay controls className="w-full mt-2 h-8" />}
                  </div>
                )}
                {it.type === "video" && (
                  <video src={it.url} controls className="w-full aspect-square object-cover bg-black" preload="metadata" />
                )}
                <div className="p-2">
                  <p className="text-xs truncate">{it.title}</p>
                  {it.ownerName && <p className="text-[10px] text-white/40 truncate">{it.ownerName}</p>}
                  <button onClick={() => download(it.url, it.title)} data-testid={`media-download-${it.id}`}
                    className="mt-1 text-[11px] text-amber-300 flex items-center gap-1 hover:text-amber-200"><Download size={12} /> Download</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {lightbox && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="media-lightbox">
          <button className="absolute top-4 right-4 text-white/70"><X size={28} /></button>
          <img src={lightbox.url} alt={lightbox.title} className="max-w-full max-h-[85vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
